import React, { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { Lock, X } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Separator,
} from '@lb-classroom/ui';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import TextInput from '../../UI/atoms/form/TextInput';
import ModalMessage from '../../UI/organisms/ModalMessage';
import LoadingMessage from '../../UI/organisms/AppLoadingMessage';
import * as StripeActions from '../../../actions/stripeMethod';
import * as ModalActions from '../../../actions/modal';
import { withTranslation, WithTranslation } from 'next-i18next';

const stripeImg = '/static/images/powered_by_stripe.png';

interface RootState {
  paymentMethod: {
    loading: boolean;
    saving: boolean;
    card: Record<string, unknown>;
    loaded: boolean;
    errorCode: string;
    errorMessage: string;
    cardToken: string;
  };
}

interface AddPaymentMethodModalOwnProps {
  addModalMessage?: (message: string, options: Record<string, unknown>) => void;
  clearModalMessage?: () => void;
  hideModal: () => void;
  isStepModal?: boolean;
  nextStep?: (e?: any) => void;
  message?: string;
  messageType?: string;
}

interface AddPaymentMethodModalStateProps {
  loadingCard?: boolean;
  savingCard?: boolean;
  stripeCard?: Record<string, unknown>;
  cardLoaded?: boolean;
  errorCode?: string;
  errorMessage?: string;
  cardToken?: string;
}

interface AddPaymentMethodModalDispatchProps {
  stripeActions: {
    addCard: (token: string, isStepModal: boolean) => void;
    requestStripeCardTokenSent: () => void;
    requestStripeCardTokenSucceeded: (data: unknown) => void;
    requestStripeCardTokenFailed: (error: unknown) => void;
    [key: string]: (...args: unknown[]) => unknown;
  };
  modalActions: Record<string, (...args: unknown[]) => unknown>;
}

type AddPaymentMethodModalContentProps = AddPaymentMethodModalOwnProps &
  AddPaymentMethodModalStateProps &
  AddPaymentMethodModalDispatchProps &
  WithTranslation;

function mapStateToProps(state: RootState) {
  const { paymentMethod } = state;
  return {
    loadingCard: paymentMethod.loading,
    savingCard: paymentMethod.saving,
    stripeCard: paymentMethod.card,
    cardLoaded: paymentMethod.loaded,
    errorCode: paymentMethod.errorCode,
    errorMessage: paymentMethod.errorMessage,
    cardToken: paymentMethod.cardToken,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    stripeActions: bindActionCreators(
      StripeActions as unknown as Record<
        string,
        (...args: unknown[]) => unknown
      >,
      dispatch,
    ),
    modalActions: bindActionCreators(
      ModalActions as unknown as Record<
        string,
        (...args: unknown[]) => unknown
      >,
      dispatch,
    ),
  };
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': {
        color: '#aab7c4',
      },
      padding: '10px 12px',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
  hidePostalCode: true,
};

const AddPaymentMethodModalContent = (
  props: AddPaymentMethodModalContentProps,
) => {
  const {
    t,
    errorCode,
    errorMessage,
    cardToken,
    stripeActions,
    modalActions,
    isStepModal = false,
    savingCard,
    loadingCard,
    hideModal,
    message,
    messageType,
  } = props;

  const stripe = useStripe();
  const elements = useElements();

  const [nameOnCard, setNameOnCard] = useState('');
  const [nameError, setNameError] = useState('');
  const [cardError, setCardError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (errorCode) {
      modalActions.addMessage(t('stripe.errors.' + errorCode), {
        type: 'error',
        autoDisappear: false,
      });
    }
  }, [errorCode, modalActions, t]);

  useEffect(() => {
    if (errorMessage) {
      modalActions.addMessage(errorMessage, {
        type: 'error',
        autoDisappear: false,
      });
      setIsSubmitting(false);
    }
  }, [errorMessage, modalActions]);

  useEffect(() => {
    if (cardToken) {
      stripeActions.addCard(cardToken, isStepModal);
    }
  }, [cardToken, isStepModal, stripeActions]);

  const handleCardChange = (event: { error?: { message: string } }) => {
    if (event.error) {
      setCardError(event.error.message);
    } else {
      setCardError('');
    }
  };

  const onSubmit = async (evt: React.FormEvent | React.KeyboardEvent) => {
    evt.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // Validate name
    if (!nameOnCard.trim()) {
      setNameError(t('validation.required'));
      return;
    }
    setNameError('');

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    setIsSubmitting(true);
    modalActions.clearMessage();
    stripeActions.requestStripeCardTokenSent();

    try {
      // Create token with card element
      const result = await stripe.createToken(cardElement, {
        name: nameOnCard.toUpperCase(),
      });

      if (result.error) {
        setCardError(result.error.message || t('stripe.errors.generic'));
        stripeActions.requestStripeCardTokenFailed({ error: result.error });
        setIsSubmitting(false);
      } else if (result.token) {
        stripeActions.requestStripeCardTokenSucceeded(result.token);
      }
    } catch (error) {
      console.error('Token creation error:', error);
      const errorMsg =
        error instanceof Error ? error.message : t('stripe.errors.generic');
      setCardError(errorMsg);
      stripeActions.requestStripeCardTokenFailed({
        error: { message: errorMsg },
      });
      setIsSubmitting(false);
    }
  };

  const nameOnCardProps = {
    placeholder: '',
    className: 'name-on-card',
    value: nameOnCard,
    name: 'credit_card__cardholder_name',
    type: 'text',
    stateType: 'nameOnCard',
    updateHandler: (value: string) => setNameOnCard(value),
    validationResult: {
      success: !nameError,
      message: nameError,
    },
    invalidLabel: false,
    attrs: {
      'aria-required': true,
    },
  };

  const wrapperClass = classnames('max-w-lg mx-auto', {
    loading: loadingCard || savingCard || isSubmitting,
  });
  const loadingMessage =
    savingCard || isSubmitting ? t('general.processing') : null;
  const loadingClass = 'modal-loading-message';

  return (
    <div className={wrapperClass}>
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>{t('student.billing.method.addMethod.title')}</CardTitle>
          <button className="modal-close" onClick={hideModal}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <LoadingMessage
            message={loadingMessage}
            loadingClass={loadingClass}
          />
          <ModalMessage message={message} messageType={messageType} />
          <form
            method="post"
            className="payment-method-form"
            onSubmit={onSubmit}
            noValidate
          >
            <p className="mb-8">
              {t('student.billing.method.addMethod.description1')}
            </p>
            <div className="block">
              <img
                src="/static/images/visa.png"
                alt="Visa logo"
                className="w-[50px] h-[30px] mr-2.5 mt-1 inline-block object-contain"
              />
              <img
                src="/static/images/master.png"
                alt="Mastercard logo"
                className="w-[50px] h-[30px] mr-2.5 mt-1 inline-block object-contain"
              />
              <img
                src="/static/images/amex.png"
                alt="Amex logo"
                className="w-[50px] h-[30px] mr-2.5 mt-1 inline-block object-contain"
              />
            </div>
            <div className="control-group">
              <label>{t('student.billing.method.addMethod.nameOnCard')}</label>
              <TextInput {...nameOnCardProps} />
              {nameError && <label className="invalid">{nameError}</label>}
            </div>
            <div className="control-group">
              <label>{t('student.billing.method.addMethod.cardDetails')}</label>
              <div className="stripe-card-element-container border rounded-md p-3 bg-white">
                <CardElement
                  options={CARD_ELEMENT_OPTIONS}
                  onChange={handleCardChange}
                />
              </div>
              {cardError && <label className="invalid">{cardError}</label>}
            </div>
            <div className="col-span-12">
              <div className="flex flex-col items-center mt-10">
                <img src={stripeImg} alt="Powered by Stripe" />
                <div className="text-secondary mt-4 flex items-center">
                  <Lock className="w-5 h-5" />
                  <span className="ml-1">
                    {t('student.billing.method.addMethod.secureTransaction')}
                  </span>
                </div>
              </div>
            </div>
            <Separator className="my-4" />
            <div className="control-group text-right">
              <Button
                className="mr-2"
                variant="outline"
                disabled={savingCard || isSubmitting}
                onClick={hideModal}
              >
                {t('links.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={!stripe || savingCard || isSubmitting}
                onClick={onSubmit}
              >
                {t('links.addCard')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const TranslatedContent = withTranslation('common')(
  AddPaymentMethodModalContent,
);
const ConnectedContent = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TranslatedContent);

// Wrapper component that provides Stripe context
const AddPaymentMethodModal = (
  props: Omit<AddPaymentMethodModalOwnProps, keyof WithTranslation>,
) => {
  // Dynamic import to avoid SSR issues with Stripe
  const [StripeProvider, setStripeProvider] = useState<React.ComponentType<{
    children: React.ReactNode;
  }> | null>(null);

  useEffect(() => {
    import('../../UI/organisms/Stripe/StripeProvider').then((module) => {
      setStripeProvider(() => module.StripeProvider);
    });
  }, []);

  if (!StripeProvider) {
    return null;
  }

  return (
    <StripeProvider>
      <ConnectedContent {...props} />
    </StripeProvider>
  );
};

export default AddPaymentMethodModal;
